import { useState, useEffect, useCallback } from 'react';
import {
  AppShell,
  Tabs,
  Button,
  Group,
  Card,
  Text,
  Badge,
  Table,
  Switch,
  ActionIcon,
  ThemeIcon,
  SimpleGrid,
  RingProgress,
  Progress,
  Stack,
  ScrollArea,
  TextInput,
  SegmentedControl,
  Divider,
  Loader,
  Modal,
  Menu,
  Select,
  NumberInput,
  Pagination,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconRefresh,
  IconLogout,
  IconSun,
  IconMoon,
  IconShield,
  IconBan,
  IconGlobe,
  IconAlertTriangle,
  IconList,
  IconServer,
  IconCheck,
  IconSearch,
  IconPlus,
  IconTrash,
  IconFolderPlus,
  IconFolder,
  IconHash,
  IconX,
  IconSettings,
  IconActivity,
  IconDotsVertical,
  IconCircleX,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { fetchAllowlist, fetchDenylist, addToList, removeFromList, createFolderInList, deleteFolderFromList, updateSettings, fetchSettings, fetchStats, fetchLogs } from '../api';
import type { Stats, LogEntry, Settings } from '../types';

interface DashboardProps {
  onLogout: () => void;
  stats: Stats | null;
  logs: LogEntry[];
  settings: Settings | null;
  onSettingsChange: (settings: Settings) => void;
}

type LogFilter = 'all' | 'allowed' | 'blocked' | 'homograph' | 'rebinding';

// Confirm Modal Component
function ConfirmModal({ opened, onClose, onConfirm, title, message }: any) {
  return (
    <Modal opened={opened} onClose={onClose} title={title} centered>
      <Text size="sm" mb="lg">{message}</Text>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button color="red" onClick={onConfirm}>Confirm</Button>
      </Group>
    </Modal>
  );
}

// Folder Manager Component
function FolderManager({ listType, data, onRefresh }: { listType: 'allowlist' | 'denylist'; data: Record<string, string[]>; onRefresh: () => void }) {
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; folder: string }>({ open: false, folder: '' });

  const folders = Object.keys(data || {}).filter(f => f !== '');
  const currentDomains = data?.[selectedFolder] || [];

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      notifications.show({ title: 'Error', message: 'Enter folder name', color: 'red' });
      return;
    }
    try {
      await createFolderInList(listType, newFolderName.trim());
      await onRefresh();
      setNewFolderName('');
      setShowNewFolder(false);
      setSelectedFolder(newFolderName.trim());
      notifications.show({ title: 'Success', message: 'Folder created', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to create folder', color: 'red' });
    }
  };

  const handleDeleteFolder = async () => {
    try {
      await deleteFolderFromList(listType, deleteModal.folder);
      await onRefresh();
      if (selectedFolder === deleteModal.folder) setSelectedFolder('');
      setDeleteModal({ open: false, folder: '' });
      notifications.show({ title: 'Success', message: 'Folder deleted', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to delete folder', color: 'red' });
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      notifications.show({ title: 'Error', message: 'Enter domain name', color: 'red' });
      return;
    }
    const domainPattern = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i;
    if (!domainPattern.test(newDomain.trim())) {
      notifications.show({ title: 'Error', message: 'Invalid domain format', color: 'red' });
      return;
    }
    setAdding(true);
    try {
      await addToList(listType, newDomain.trim(), selectedFolder);
      await onRefresh();
      setNewDomain('');
      setShowAddDomain(false);
      notifications.show({ title: 'Success', message: 'Domain added', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to add domain', color: 'red' });
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveDomain = async (folder: string, domain: string) => {
    try {
      await removeFromList(listType, folder, domain);
      await onRefresh();
      notifications.show({ title: 'Success', message: `Removed ${domain}`, color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to remove domain', color: 'red' });
    }
  };

  useEffect(() => {
    const firstFolder = folders[0];
    if (firstFolder && !selectedFolder) setSelectedFolder(firstFolder);
  }, [data]);

  return (
    <>
      <ConfirmModal
        opened={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, folder: '' })}
        onConfirm={handleDeleteFolder}
        title="Delete Folder"
        message={`Delete "${deleteModal.folder}" and all its domains? This cannot be undone.`}
      />

      <Card withBorder radius="lg" p="lg">
        <Group justify="space-between" mb="md">
          <Group>
            <IconFolder size={20} />
            <Text fw={600}>Folders</Text>
            <Badge variant="light">{folders.length}</Badge>
          </Group>
          <Button variant="light" size="xs" leftSection={<IconFolderPlus size={14} />} onClick={() => setShowNewFolder(!showNewFolder)}>
            New Folder
          </Button>
        </Group>

        {showNewFolder && (
          <Group mb="md">
            <TextInput
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              style={{ flex: 1 }}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <Button onClick={handleCreateFolder}>Create</Button>
            <ActionIcon onClick={() => setShowNewFolder(false)}><IconX size={16} /></ActionIcon>
          </Group>
        )}

        <ScrollArea>
          <Group gap="xs" mb="lg" style={{ flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 8 }}>
            <Button
              variant={selectedFolder === '' ? 'filled' : 'light'}
              size="compact-sm"
              onClick={() => setSelectedFolder('')}
              leftSection={<IconHash size={14} />}
            >
              default ({data?.['']?.length || 0})
            </Button>
            {folders.map((folder) => (
              <Menu key={folder} position="bottom-end">
                <Menu.Target>
                  <Button
                    variant={selectedFolder === folder ? 'filled' : 'light'}
                    size="compact-sm"
                    leftSection={<IconFolder size={14} />}
                    rightSection={<IconDotsVertical size={12} />}
                    onClick={() => setSelectedFolder(folder)}
                  >
                    {folder} ({data?.[folder]?.length || 0})
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => setDeleteModal({ open: true, folder })}>
                    Delete Folder
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ))}
          </Group>
        </ScrollArea>

        <Divider my="md" />

        <Group justify="space-between" mb="md">
          <Text fw={500}>Domains in "{selectedFolder === '' ? 'default' : selectedFolder}"</Text>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setShowAddDomain(!showAddDomain)}>
            Add Domain
          </Button>
        </Group>

        {showAddDomain && (
          <Group mb="md">
            <TextInput
              placeholder="example.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              style={{ flex: 1 }}
              onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
            />
            <Button onClick={handleAddDomain} loading={adding}>Add</Button>
            <ActionIcon onClick={() => setShowAddDomain(false)}><IconX size={16} /></ActionIcon>
          </Group>
        )}

        <ScrollArea h={300}>
          {currentDomains.length === 0 ? (
            <Text ta="center" c="dimmed" py="xl">No domains in this folder</Text>
          ) : (
            <Stack gap="xs">
              {currentDomains.map((domain) => (
                <Group key={domain} justify="space-between" p="xs" style={{ borderRadius: 8, background: 'var(--mantine-color-gray-0)' }}>
                  <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>{domain}</Text>
                  <ActionIcon color="red" variant="subtle" onClick={() => handleRemoveDomain(selectedFolder, domain)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          )}
        </ScrollArea>
      </Card>
    </>
  );
}

// Settings Panel Component
function SettingsPanel({ settings, onSave }: { settings: Settings | null; onSave: (settings: Settings) => void }) {
  const [localSettings, setLocalSettings] = useState<Settings | null>(settings);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!localSettings) return;
    setSaving(true);
    try {
      await updateSettings(localSettings);
      onSave(localSettings);
      notifications.show({ title: 'Success', message: 'Settings saved', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save settings', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const dohPresets = [
    { label: 'Cloudflare Standard', value: 'https://cloudflare-dns.com/dns-query' },
    { label: 'Cloudflare Security', value: 'https://security.cloudflare-dns.com/dns-query' },
    { label: 'Cloudflare Family', value: 'https://family.cloudflare-dns.com/dns-query' },
    { label: 'Google Public DNS', value: 'https://dns.google/dns-query' },
    { label: 'Quad9 Standard', value: 'https://dns.quad9.net/dns-query' },
    { label: 'Quad9 (ECS Enabled)', value: 'https://dns10.quad9.net/dns-query' },
    { label: 'Quad9 Unfiltered', value: 'https://dns11.quad9.net/dns-query' },
  ];

  if (!localSettings) return <Loader />;

  return (
    <Card withBorder radius="lg" p="lg">
      <Text size="lg" fw={700} mb="md">DNS Gateway Settings</Text>
      
      <Stack gap="lg">
        <div>
          <Text fw={500} mb="xs">Upstream DNS (DoH)</Text>
          <Select
            placeholder="Select preset"
            data={dohPresets}
            value={localSettings.upstream}
            onChange={(val: string | null) => val && setLocalSettings({ ...localSettings, upstream: val })}
            mb="xs"
          />
          <TextInput
            placeholder="Custom HTTPS URL"
            value={localSettings.upstream}
            onChange={(e) => setLocalSettings({ ...localSettings, upstream: e.target.value })}
          />
        </div>

        <div>
          <Text fw={500} mb="xs">Block TTL (seconds)</Text>
          <NumberInput
            value={localSettings.ttl.block}
            onChange={(val: string | number) => setLocalSettings({ ...localSettings, ttl: { block: Number(val) || 0 } })}
            min={0}
            max={86400}
          />
          <Text size="xs" c="dimmed" mt="4">0-86400 seconds</Text>
        </div>

        <Divider />

        <div>
          <Text fw={500} mb="sm">Protections</Text>
          <Switch
            label="DNS Rebinding Protection"
            description="Blocks DNS responses pointing to private IP ranges"
            checked={localSettings.protections.rebinding}
            onChange={(e) => setLocalSettings({
              ...localSettings,
              protections: { ...localSettings.protections, rebinding: e.currentTarget.checked }
            })}
            mb="md"
          />
          <Switch
            label="Homograph Attack Protection"
            description="Blocks punycode domains that mimic legitimate ones"
            checked={localSettings.protections.homograph}
            onChange={(e) => setLocalSettings({
              ...localSettings,
              protections: { ...localSettings.protections, homograph: e.currentTarget.checked }
            })}
          />
        </div>

        <Divider />

        <div>
          <Text fw={500} mb="sm">Features</Text>
          <Switch
            label="Drop IPv6 Queries"
            description="Return REFUSED for AAAA (IPv6) records"
            checked={localSettings.features.dropIPv6}
            onChange={(e) => setLocalSettings({
              ...localSettings,
              features: { dropIPv6: e.currentTarget.checked }
            })}
          />
        </div>

        <Button onClick={handleSave} loading={saving} variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
          Save Settings
        </Button>
      </Stack>
    </Card>
  );
}

// Get status badge color based on reason
function getStatusBadge(reason: string, blocked: number) {
  if (!blocked) return { color: 'green', label: 'Allowed', variant: 'light' as const };
  
  switch (reason) {
    case 'allowlist':
      return { color: 'green', label: 'Allowlist', variant: 'filled' as const };
    case 'denylist':
      return { color: 'red', label: 'Denylist', variant: 'filled' as const };
    case 'blocklist':
      return { color: 'red', label: 'Blocklist', variant: 'light' as const };
    case 'homograph':
      return { color: 'yellow', label: 'Homograph', variant: 'light' as const };
    case 'rebinding':
      return { color: 'purple', label: 'Rebinding', variant: 'light' as const };
    default:
      return { color: 'gray', label: 'Blocked', variant: 'light' as const };
  }
}

export default function Dashboard({ onLogout, onSettingsChange }: DashboardProps) {
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [darkMode, setDarkMode] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [logFilter, setLogFilter] = useState<LogFilter>('all');
  const [logPage, setLogPage] = useState(1);
  const [allowlistData, setAllowlistData] = useState<Record<string, string[]>>({});
  const [denylistData, setDenylistData] = useState<Record<string, string[]>>({});
  const [loadingLists, setLoadingLists] = useState(false);
  const [cachedStats, setCachedStats] = useState<Stats | null>(null);
  const [cachedLogs, setCachedLogs] = useState<LogEntry[]>([]);
  const [cachedSettings, setCachedSettings] = useState<Settings | null>(null);
  const itemsPerPage = 25;

  // Load ALL data upfront (background caching)
  const loadAllData = useCallback(async (showToast = false) => {
    try {
      const [statsData, logsData, settingsData, allowData, denyData] = await Promise.all([
        fetchStats(),
        fetchLogs(500),
        fetchSettings(),
        fetchAllowlist(),
        fetchDenylist()
      ]);
      
      setCachedStats(statsData);
      setCachedLogs(logsData.logs);
      setCachedSettings(settingsData);
      setAllowlistData(allowData);
      setDenylistData(denyData);
      onSettingsChange(settingsData);
      
      if (showToast) {
        notifications.show({ title: 'Refreshed', message: 'Dashboard data updated', color: 'green', icon: <IconCheck size={16} /> });
      }
    } catch (error) {
      if (showToast) {
        notifications.show({ title: 'Error', message: 'Failed to refresh data', color: 'red' });
      }
    }
  }, [onSettingsChange]);

  // Initial load - cache everything
  useEffect(() => {
    loadAllData(false);
  }, []);

  // Auto-refresh (refreshes everything)
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      loadAllData(false);
    }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadAllData]);

  // Load lists only when needed (already cached, but refresh on tab change)
  const loadLists = async () => {
    setLoadingLists(true);
    try {
      const [allow, deny] = await Promise.all([fetchAllowlist(), fetchDenylist()]);
      setAllowlistData(allow);
      setDenylistData(deny);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load lists', color: 'red' });
    } finally {
      setLoadingLists(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'allowlist' || activeTab === 'denylist') {
      loadLists();
    }
  }, [activeTab]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData(true);
    setRefreshing(false);
  };

  const toggleAutoRefresh = async () => {
    const newValue = !autoRefresh;
    setAutoRefresh(newValue);
    try {
      await updateSettings({ autoRefresh: newValue });
      if (cachedSettings) onSettingsChange({ ...cachedSettings, autoRefresh: newValue });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save preference', color: 'red' });
    }
  };

  // Handle quick actions (Pi-hole style)
  const handleQuickAction = async (domain: string, currentReason: string, blocked: number) => {
    try {
      if (!blocked || currentReason === 'none') {
        // Block this domain - add to denylist
        await addToList('denylist', domain, '');
        notifications.show({ 
          title: 'Domain Blocked', 
          message: `${domain} added to denylist`,
          color: 'red',
          icon: <IconBan size={16} />
        });
        await loadAllData(false);
        if (activeTab === 'denylist') await loadLists();
      } else if (currentReason === 'blocklist' || currentReason === 'homograph' || currentReason === 'rebinding') {
        // Allow this domain - add to allowlist
        await addToList('allowlist', domain, '');
        notifications.show({ 
          title: 'Domain Allowed', 
          message: `${domain} added to allowlist`,
          color: 'green',
          icon: <IconShield size={16} />
        });
        await loadAllData(false);
        if (activeTab === 'allowlist') await loadLists();
      } else if (currentReason === 'allowlist' || currentReason === 'denylist') {
        // Remove from list
        await removeFromList(currentReason === 'allowlist' ? 'allowlist' : 'denylist', '', domain);
        notifications.show({ 
          title: 'Domain Removed', 
          message: `${domain} removed from ${currentReason}`,
          color: 'yellow',
          icon: <IconCircleX size={16} />
        });
        await loadAllData(false);
        if (activeTab === 'allowlist' || activeTab === 'denylist') await loadLists();
      }
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Action failed', color: 'red' });
    }
  };

  // Get quick action button config
  const getQuickActionButton = (reason: string, blocked: number, domain: string) => {
    if (!blocked || reason === 'none') {
      return {
        label: 'Block',
        color: 'red',
        variant: 'outline' as const,
        icon: <IconBan size={14} />,
        action: () => handleQuickAction(domain, reason, blocked)
      };
    } else if (reason === 'blocklist' || reason === 'homograph' || reason === 'rebinding') {
      return {
        label: 'Allow',
        color: 'green',
        variant: 'light' as const,
        icon: <IconPlayerPlay size={14} />,
        action: () => handleQuickAction(domain, reason, blocked)
      };
    } else {
      return {
        label: 'Remove',
        color: 'gray',
        variant: 'subtle' as const,
        icon: <IconTrash size={14} />,
        action: () => handleQuickAction(domain, reason, blocked)
      };
    }
  };

  // Filter logs
  const filteredLogs = (cachedLogs || []).filter(log => {
    const matchesSearch = log.domain.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = logFilter === 'all' ? true :
      logFilter === 'allowed' ? !log.blocked :
      logFilter === 'blocked' ? log.blocked && !['homograph', 'rebinding'].includes(log.reason) :
      logFilter === 'homograph' ? log.reason === 'homograph' :
      log.reason === 'rebinding';
    return matchesSearch && matchesFilter;
  });

  // Paginate logs
  const paginatedLogs = filteredLogs.slice((logPage - 1) * itemsPerPage, logPage * itemsPerPage);
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);

  const blockRate = cachedStats && cachedStats.stats && cachedStats.stats.total > 0 
    ? ((cachedStats.stats.blocked / cachedStats.stats.total) * 100).toFixed(0) 
    : '0';

  useEffect(() => {
    document.documentElement.setAttribute('data-mantine-color-scheme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Reset page when filter changes
  useEffect(() => {
    setLogPage(1);
  }, [searchQuery, logFilter]);

  return (
    <AppShell
      padding="md"
      style={{ minHeight: '100vh' }}
    >
      <AppShell.Header p="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)', zIndex: 200 }}>
        <Group justify="space-between" h="100%">
          <Group>
            <ThemeIcon size="lg" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }} radius="md">
              <IconServer size={20} />
            </ThemeIcon>
            <div>
              <Text size="lg" fw={700}>Sinker DNS Gateway</Text>
              <Text size="xs" c="dimmed">Real-time DNS Protection Dashboard</Text>
            </div>
          </Group>
          
          <Group>
            <Group gap="xs">
              <Text size="sm" c="dimmed">Auto-refresh</Text>
              <Switch
                size="lg"
                checked={autoRefresh}
                onChange={toggleAutoRefresh}
              />
            </Group>
            
            <ActionIcon variant="light" size="lg" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>
            
            <ActionIcon variant="light" color="blue" size="lg" onClick={handleRefresh} loading={refreshing}>
              <IconRefresh size={18} />
            </ActionIcon>
            
            <Button variant="light" color="red" onClick={onLogout} leftSection={<IconLogout size={16} />}>
              Logout
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main pt={60}>
        <Tabs value={activeTab} onChange={setActiveTab} variant="pills" radius="xl">
          <ScrollArea type="scroll" scrollbarSize={4} offsetScrollbars>
            <Tabs.List grow mb="xl" style={{ flexWrap: 'nowrap', minWidth: 'max-content' }}>
              <Tabs.Tab value="overview" leftSection={<IconActivity size={16} />}>Overview</Tabs.Tab>
              <Tabs.Tab value="logs" leftSection={<IconList size={16} />}>
                Query Logs
                <Badge size="xs" ml="xs" variant="light">{filteredLogs.length}</Badge>
              </Tabs.Tab>
              <Tabs.Tab value="allowlist" leftSection={<IconShield size={16} />}>Allowlist</Tabs.Tab>
              <Tabs.Tab value="denylist" leftSection={<IconBan size={16} />}>Denylist</Tabs.Tab>
              <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>Settings</Tabs.Tab>
            </Tabs.List>
          </ScrollArea>

          {/* Overview Tab */}
          <Tabs.Panel value="overview">
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg" mb="xl">
              <Card withBorder radius="lg" padding="lg" style={{ transition: 'transform 0.2s' }}>
                <Group justify="space-between" mb="xs">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Queries</Text>
                  <ThemeIcon color="blue" variant="light" radius="md" size="lg"><IconGlobe size={20} /></ThemeIcon>
                </Group>
                <Text size="xl" fw={700}>{cachedStats?.stats?.total?.toLocaleString() || 0}</Text>
              </Card>

              <Card withBorder radius="lg" padding="lg">
                <Group justify="space-between" mb="xs">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Blocked</Text>
                  <ThemeIcon color="red" variant="light" radius="md" size="lg"><IconBan size={20} /></ThemeIcon>
                </Group>
                <Text size="xl" fw={700} c="red">{cachedStats?.stats?.blocked?.toLocaleString() || 0}</Text>
              </Card>

              <Card withBorder radius="lg" padding="lg">
                <Group justify="space-between" mb="xs">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Block Rate</Text>
                  <ThemeIcon color="orange" variant="light" radius="md" size="lg"><IconShield size={20} /></ThemeIcon>
                </Group>
                <Text size="xl" fw={700}>{blockRate}%</Text>
              </Card>

              <Card withBorder radius="lg" padding="lg">
                <Group justify="space-between" mb="xs">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Threats</Text>
                  <ThemeIcon color="yellow" variant="light" radius="md" size="lg"><IconAlertTriangle size={20} /></ThemeIcon>
                </Group>
                <Text size="xl" fw={700}>{(cachedStats?.stats?.rebinding || 0) + (cachedStats?.stats?.homograph || 0)}</Text>
              </Card>
            </SimpleGrid>

            <Card withBorder radius="lg" padding="xl">
              <Group justify="space-between" mb="md">
                <Text fw={600} size="lg">Protection Efficiency</Text>
                <Badge size="lg" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>99.9% Uptime</Badge>
              </Group>
              
              <Group justify="center" mb="xl">
                <RingProgress
                  size={180}
                  thickness={12}
                  label={<Text ta="center" fw={700} size="xl">{blockRate}%</Text>}
                  sections={[{ value: Number(blockRate), color: 'red' }]}
                />
              </Group>
              
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">Blocklist Coverage</Text>
                  <Text size="sm" fw={500}>85%</Text>
                </Group>
                <Progress value={85} animated striped />
                
                <Group justify="space-between" mt="xs">
                  <Text size="sm">Security Score</Text>
                  <Text size="sm" fw={500}>A+</Text>
                </Group>
                <Progress value={98} color="green" animated />
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* Logs Tab with Search, Filters, Pagination, and Quick Actions */}
          <Tabs.Panel value="logs">
            <Card withBorder radius="lg" padding="xl">
              <Group justify="space-between" mb="lg">
                <Text fw={600} size="lg">Query Logs</Text>
                <Group>
                  <TextInput
                    placeholder="Search domains..."
                    leftSection={<IconSearch size={16} />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: 250 }}
                  />
                  <SegmentedControl
                    value={logFilter}
                    onChange={(val) => setLogFilter(val as LogFilter)}
                    data={[
                      { label: 'All', value: 'all' },
                      { label: '✅ Allowed', value: 'allowed' },
                      { label: '🚫 Blocked', value: 'blocked' },
                      { label: '⚠️ Homograph', value: 'homograph' },
                      { label: '🔄 Rebinding', value: 'rebinding' },
                    ]}
                  />
                </Group>
              </Group>

              <ScrollArea h={450}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Domain</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Action</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedLogs.map((log) => {
                      const status = getStatusBadge(log.reason, log.blocked);
                      const actionBtn = getQuickActionButton(log.reason, log.blocked, log.domain);
                      return (
                        <Table.Tr key={log.id}>
                          <Table.Td><Text fw={500} size="sm" style={{ fontFamily: 'monospace' }}>{log.domain}</Text></Table.Td>
                          <Table.Td>
                            <Badge color={status.color} variant={status.variant}>
                              {status.label}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Button
                              size="compact-xs"
                              color={actionBtn.color}
                              variant={actionBtn.variant}
                              leftSection={actionBtn.icon}
                              onClick={actionBtn.action}
                            >
                              {actionBtn.label}
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
                {filteredLogs.length === 0 && (
                  <Text ta="center" c="dimmed" py="xl">No logs match your filters</Text>
                )}
              </ScrollArea>

              {totalPages > 1 && (
                <Group justify="center" mt="lg">
                  <Pagination
                    total={totalPages}
                    value={logPage}
                    onChange={setLogPage}
                    withEdges
                    size="sm"
                  />
                </Group>
              )}
            </Card>
          </Tabs.Panel>

          {/* Allowlist Tab */}
          <Tabs.Panel value="allowlist">
            {loadingLists ? (
              <Group justify="center" py="xl"><Loader /></Group>
            ) : (
              <FolderManager listType="allowlist" data={allowlistData} onRefresh={loadLists} />
            )}
          </Tabs.Panel>

          {/* Denylist Tab */}
          <Tabs.Panel value="denylist">
            {loadingLists ? (
              <Group justify="center" py="xl"><Loader /></Group>
            ) : (
              <FolderManager listType="denylist" data={denylistData} onRefresh={loadLists} />
            )}
          </Tabs.Panel>

          {/* Settings Tab */}
          <Tabs.Panel value="settings">
            <SettingsPanel settings={cachedSettings} onSave={onSettingsChange} />
          </Tabs.Panel>
        </Tabs>
      </AppShell.Main>
    </AppShell>
  );
}
